import { EuiFlexGroup, EuiFlexItem } from "@elastic/eui";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import trackAnalytics from "trackAnalytics";
import { useMapDataToUiWithFilter } from "hooks/mapDataToOptions";
import { ButtonRow } from "styles/elements/main";
import { Button, SelectWithSearch } from "components";
import FilterLabel from "modules/shared/profiles/filters/FilterLabel";
import { PROJECT_TYPES, PAST_PERIODS, UPCOMING_PERIODS, PROJECT_STATUS } from "../../staticData";
const ProjectsFilters = ({ setProjectFilterOptions, filterOptions }) => {
    const {
        data: organizations,
        setSearchFilter: setOrganizationsFilter,
        clearSearchFilter: clearOrganizationsFilter,
    } = useMapDataToUiWithFilter("organizations");
    const {
        data: admins,
        setSearchFilter: setAdminsFilter,
        clearSearchFilter: clearAdminsFilter,
    } = useMapDataToUiWithFilter("admin-users", "adminUsers");
    const [filters, setFilters] = useState({});
    const [totalFilters, setTotalFilters] = useState(0);
    const location = useLocation();
    const isInClientPage = location.pathname.includes("clients");

    const analyticsEvents = {
        organizationId: "Filter_projects_by_client_clicked",
        projectType: "Filter_projects_by_project_type_clicked",
        status: "Filter_projects_by_status_clicked",
        creatorId: "Filter_projects_by_creator_email_clicked",
        requested_at: "Filter_projects_by_requested_at_clicked",
        created_at: "Filter_projects_by_created_at_clicked",
        starting_at: "Filter_projects_by_starting_at_clicked",
    };

    useEffect(() => {
        const sum = Object?.values(filters)?.reduce((acc, curr) => acc + curr?.value?.length, 0);
        setTotalFilters(sum);
    }, [filterOptions]);

    const handelClearAllFilters = () => {
        const updatedFilters = {};
        for (const filter in filters) {
            updatedFilters[filter] = { value: [] };
        }
        setFilters(updatedFilters);
        setProjectFilterOptions([]);
    };

    const handleClearSingleFilter = (filterName) => {
        const filtered = { ...filterOptions[0] };
        delete filtered[filterName];
        setFilters((prev) => ({ ...prev, [filterName]: { value: [] } }));
        setProjectFilterOptions([filtered]);
    };

    const handleApplyFilter = (id, name, newFilters) => {
        const appliedOptions = [];
        let filterValues = [];
        const updatedFilters = newFilters || filters;
        // eslint-disable-next-line no-restricted-syntax
        for (const option in updatedFilters) {
            if (updatedFilters[option].id === id) {
                const filter = updatedFilters[option];
                filterValues = filter.value.map((element) => element.label);
                appliedOptions.push({ ...updatedFilters, [option]: filter });
            }
        }
        if (analyticsEvents[name]) {
            trackAnalytics(analyticsEvents[name], {
                "Filter Value": filterValues,
            });
        }
        setProjectFilterOptions(appliedOptions);
    };

    const handleChangedData = (value, name, id) => {
        const newFilters = {
            ...filters,
            [name]: { value, id },
        };
        setFilters(newFilters);
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < filterOptions?.length; i++) {
            if (newFilters[name]?.value?.length < filterOptions[i][name]?.value?.length) {
                handleApplyFilter(id, name, newFilters);
            }
        }
    };

    const constantsCustomSearchFilter = (filterType, value) => {
        return filterType.filter((item) => item.label.toLowerCase().includes(value.toLowerCase()));
    };

    const ActionBtns = ({ name, id }) => (
        <ButtonRow>
            <Button
                name="Clear"
                type="button"
                btnStyle="secondBtn"
                onChange={() => handleClearSingleFilter(name)}
            />
            <Button
                name="Apply"
                onChange={() => handleApplyFilter(id, name)}
                type="button"
                btnStyle="primaryBtn"
            />
        </ButtonRow>
    );
    return (
        <>
            <FilterLabel
                handelClearAllFilters={handelClearAllFilters}
                filtersCount={totalFilters}
            />
            <EuiFlexGroup
                className="step-container"
                gutterSize="s"
            >
                {!isInClientPage && (
                    <EuiFlexItem>
                        <SelectWithSearch
                            placeholder="Client"
                            name="organizationId"
                            isEditable
                            customBtns={
                                <ActionBtns
                                    name="organizationId"
                                    id={1}
                                />
                            }
                            isMulti
                            value={filters?.organizationId?.value}
                            options={organizations}
                            handleChange={(value) => {
                                handleChangedData(value, "organizationId", 1);
                                clearOrganizationsFilter();
                            }}
                            handleFilter={(filter) => {
                                setOrganizationsFilter(filter);
                                return organizations;
                            }}
                            onMenuClose={() => clearOrganizationsFilter()}
                        />
                    </EuiFlexItem>
                )}
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Project Type"
                        name="projectType"
                        customBtns={
                            <ActionBtns
                                name="projectType"
                                id={2}
                            />
                        }
                        isEditable
                        value={filters?.projectType?.value}
                        isMulti
                        handleChange={(value) => handleChangedData(value, "projectType", 2)}
                        handleFilter={(filter) =>
                            constantsCustomSearchFilter(PROJECT_TYPES, filter)
                        }
                        options={PROJECT_TYPES}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Status"
                        name="status"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="status"
                                id={3}
                            />
                        }
                        isMulti
                        value={filters?.status?.value}
                        handleChange={(value) => handleChangedData(value, "status", 3)}
                        handleFilter={(filter) =>
                            constantsCustomSearchFilter(PROJECT_STATUS, filter)
                        }
                        options={PROJECT_STATUS}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Creator Email"
                        name="creatorId"
                        customBtns={
                            <ActionBtns
                                name="creatorId"
                                id={4}
                            />
                        }
                        isEditable
                        value={filters?.creatorId?.value}
                        isMulti
                        handleChange={(value) => {
                            handleChangedData(value, "creatorId", 4);
                            clearAdminsFilter();
                        }}
                        handleFilter={(filter) => {
                            setAdminsFilter(filter);
                            return admins;
                        }}
                        options={admins}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Requested At"
                        name="requested_at"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="requested_at"
                                id={5}
                            />
                        }
                        isMulti
                        value={filters?.requested_at?.value}
                        handleChange={(value) => handleChangedData(value, "requested_at", 5)}
                        handleFilter={(filter) => constantsCustomSearchFilter(PAST_PERIODS, filter)}
                        options={PAST_PERIODS}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Created At"
                        name="created_at"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="created_at"
                                id={6}
                            />
                        }
                        isMulti
                        value={filters?.created_at?.value}
                        handleChange={(value) => handleChangedData(value, "created_at", 6)}
                        handleFilter={(filter) => constantsCustomSearchFilter(PAST_PERIODS, filter)}
                        options={PAST_PERIODS}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Starting At"
                        name="starting_at"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="starting_at"
                                id={7}
                            />
                        }
                        isMulti
                        value={filters?.starting_at?.value}
                        handleChange={(value) => handleChangedData(value, "starting_at", 7)}
                        handleFilter={(filter) =>
                            constantsCustomSearchFilter(UPCOMING_PERIODS, filter)
                        }
                        options={UPCOMING_PERIODS}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>
        </>
    );
};

export default ProjectsFilters;
